import {
  createConfig,
  deleteConfig,
  getConfig,
  getConfigByTypeKey,
  getConfigs,
  updateConfig,
} from "./configurationService";
import { getCoreSkills } from "./skillCoreService";

const SKILL_TYPE = "skill";

const viewScope = [
  "owner",
  "admin",
  "prompt-engineer",
  "clinical-author",
  "demonstrator",
  "viewer",
];
const editScope = ["owner", "admin", "prompt-engineer"];
const deleteScope = ["owner", "admin", "prompt-engineer"];

export const skillService = {
  create: async ({
    key,
    description = "",
    preProcessing = false,
    postProcessing = true,
    tags = [],
    example = "",
    overloads = null,
  }) => {
    if (!key) {
      throw new Error("Key is required");
    }
    try {
      const existingSkill = await getConfigByTypeKey(SKILL_TYPE, key);
      if (existingSkill) {
        throw new Error("Key is already in use");
      }
    } catch {}
    const coreSkills = await getCoreSkills({});
    const coreSkill = coreSkills.find((coreSkill) => coreSkill.key === key);
    if (coreSkill) {
      if (!tags.includes("core")) {
        tags.push("core");
      }
    }
    return await createConfig(
      SKILL_TYPE,
      key,
      JSON.stringify({
        description: description, // this property is customizable for a core skill
        preProcessing: coreSkill ? coreSkill.preProcessing : preProcessing, // this property is not overwritten from the core skill
        postProcessing: coreSkill ? coreSkill.postProcessing : postProcessing, // this property is not overwritten from the core skill
        tags: tags, // this property is customizable for a core skill
        example: example, // this property is customizable for a core skill
        core: coreSkill ? true : false, // this indicates if the skill is a core skill
        custom: true, // this indicates that this is a custom skill
        overloads: overloads, // this represents the core skill key that will overload the custom skill
      }),
      null,
      null,
      viewScope,
      editScope,
      deleteScope
    );
  },

  list: async () => {
    // get the core skills
    const coreSkills = await getCoreSkills({});

    // get the custom skills
    const results = await getConfigs(SKILL_TYPE);
    const customSkills = results.map((result) => {
      const value = JSON.parse(result.value);
      return {
        id: result.id,
        key: result.key,
        description: value.description,
        preProcessing: value.preProcessing,
        postProcessing: value.postProcessing,
        tags: value.tags,
        example: value.example,
        overloads: value.overloads,
      };
    });

    // create a list of the aggregate skills, if a core skill has a custom skill with the same key, the custom skill will be used
    const skills = coreSkills.map((coreSkill) => {
      const customSkill = customSkills.find(
        (customSkill) => customSkill.key === coreSkill.key
      );
      if (customSkill) {
        customSkill.core = true;
        customSkill.custom = true;
        if (!customSkill.tags.includes("core")) customSkill.tags.push("core");
      }
      return customSkill ? customSkill : coreSkill;
    });
    // now add in any custom skills that are not core skills
    customSkills.forEach((customSkill) => {
      if (!skills.find((skill) => skill.key === customSkill.key)) {
        customSkill.core = false;
        customSkill.custom = true;
        skills.push(customSkill);
      }
    });

    // remove any empty tags from the skills
    skills.forEach((skill) => {
      skill.tags = skill.tags.filter((tag) => tag.trim() !== "");
    });

    return skills;
  },

  get: async (key, throw404 = false) => {
    const coreSkills = await getCoreSkills({});
    try {
      const result = await getConfigByTypeKey(SKILL_TYPE, key);
      const custom = JSON.parse(result.value);
      const isCore = coreSkills.find((coreSkill) => coreSkill.key === key)
        ? true
        : false;
      if (isCore && !custom.tags.includes("core")) {
        custom.tags.push("core");
      }
      return {
        core: coreSkills.find((coreSkill) => coreSkill.key === key)
          ? true
          : false,
        custom: true,
        id: result.id,
        key: result.key,
        description: custom.description,
        preProcessing: custom.preProcessing,
        postProcessing: custom.postProcessing,
        tags: custom.tags,
        example: custom.example,
      };
    } catch (e) {
      if (throw404 && e.response && e.response.status === 404) {
        throw e;
      }
    }

    // if a custom skill does not exist, get the core skill
    const coreSkill = coreSkills.find((coreSkill) => coreSkill.key === key);
    if (coreSkill) {
      return coreSkill;
    }

    // if the skill does not exist, return null
    return null;
  },

  update: async ({
    id,
    key,
    description = "",
    preProcessing = false,
    postProcessing = true,
    tags = [],
    example = "",
    overloads = null,
  }) => {
    // ensure the key is provided
    if (!key) {
      throw new Error("Key is required");
    }

    // ensure the key is unique
    try {
      const findSkill = await getConfigByTypeKey(SKILL_TYPE, key);
      if (findSkill && findSkill.id !== id) {
        throw new Error("Key is already in use by a custom skill");
      }
    } catch (e) {
      if (e.response && e.response.status === 404) {
        // skip 404 errors
      } else {
        throw e;
      }
    }

    // ensure the skill exists
    const existingSkill = await getConfig(id);
    if (!existingSkill) {
      throw new Error("Skill does not exist");
    }

    // if the skill does not exist, call the create method instead
    if (!existingSkill) {
      return await skillService.create({
        key: key,
        description: description,
        preProcessing: preProcessing,
        postProcessing: postProcessing,
        tags: tags,
        example: example,
        overloads: overloads,
      });
    }

    // see if this is a core skill
    const coreSkills = await getCoreSkills({});
    const coreSkill = coreSkills.find((coreSkill) => coreSkill.key === key);
    if (coreSkill) {
      if (!tags.includes("core")) {
        tags.push("core");
      }
    }

    // update the skill
    return await updateConfig(
      id,
      SKILL_TYPE,
      key,
      JSON.stringify({
        description: description,
        preProcessing: coreSkill ? coreSkill.preProcessing : preProcessing,
        postProcessing: coreSkill ? coreSkill.postProcessing : postProcessing,
        tags: tags,
        example: example,
        core: coreSkill ? true : false,
        custom: true,
        overloads: overloads,
      })
    );
  },

  delete: async (id) => {
    const skill = await getConfig(id);
    if (skill) {
      return await deleteConfig(id);
    }
    return null;
  },

  duplicate: async (id) => {
    const skill = await getConfig(id);
    const rand = Math.floor(1000 + Math.random() * 9000);
    if (skill) {
      const key = `Copy of ${skill.value.name} ${rand}`;
      return await createConfig(
        SKILL_TYPE,
        key,
        skill.value,
        null,
        null,
        viewScope,
        editScope,
        deleteScope
      );
    }
    return null;
  },
};

export default skillService;
